// src/contexts/ExportContext.js
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import ExportProgressModal from '../components/common/ExportProgressModal/ExportProgressModal';
import { useAlert } from './AlertContext';
import { useLocalization } from './LocalizationContext';
import styles from '../components/common/ExportProgressModal/ExportProgressModal.module.css';

const ExportContext = createContext(null);

const IDLE_JOB = { isOpen: false, isMinimized: false, status: 'idle', error: '', total: 0, fetched: 0, title: '' };

/**
 * Runs one export at a time outside any page, so a minimized export keeps
 * running (and still downloads) after the user navigates away.
 * runner({ signal, onTotal, onProgress }) must stop when signal is aborted.
 */
export const ExportProvider = ({ children }) => {
  const { t } = useLocalization();
  const { success } = useAlert();
  const [job, setJob] = useState(IDLE_JOB);
  const controllerRef = useRef(null);
  const minimizedRef = useRef(false);

  const startExport = useCallback(async (runner, { title } = {}) => {
    if (controllerRef.current) return false;

    const controller = new AbortController();
    controllerRef.current = controller;
    minimizedRef.current = false;
    setJob({ ...IDLE_JOB, isOpen: true, status: 'running', title });

    try {
      await runner({
        signal: controller.signal,
        onTotal: (total) => { if (!controller.signal.aborted) setJob(prev => ({ ...prev, total })); },
        onProgress: (fetched) => { if (!controller.signal.aborted) setJob(prev => ({ ...prev, fetched })); },
      });
      if (controller.signal.aborted) return false;

      if (minimizedRef.current) {
        setJob(IDLE_JOB);
        success(t('ecomOrders.exportCompleteBackground') || 'Export complete. Your file has been downloaded.');
      } else {
        setJob(prev => ({ ...prev, status: 'done' }));
      }
      return true;
    } catch (err) {
      if (controller.signal.aborted) return false;
      setJob(prev => ({
        ...prev,
        isOpen: true,
        isMinimized: false,
        status: 'error',
        error: err?.message || 'Export failed. Please try again.',
      }));
      return false;
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
    }
  }, [success, t]);

  const cancelExport = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    minimizedRef.current = false;
    setJob(IDLE_JOB);
  }, []);

  const closeExport = useCallback(() => {
    minimizedRef.current = false;
    setJob(IDLE_JOB);
  }, []);

  const minimizeExport = useCallback(() => {
    minimizedRef.current = true;
    setJob(prev => ({ ...prev, isMinimized: true }));
  }, []);

  const restoreExport = useCallback(() => {
    minimizedRef.current = false;
    setJob(prev => ({ ...prev, isMinimized: false }));
  }, []);

  const isRunning = job.status === 'running';
  const pct = job.total > 0 ? Math.round((job.fetched / job.total) * 100) : 0;

  return (
    <ExportContext.Provider value={{ startExport, cancelExport, isExporting: isRunning }}>
      {children}

      <ExportProgressModal
        isOpen={job.isOpen && !job.isMinimized}
        title={job.title || undefined}
        status={job.status}
        error={job.error}
        totalRecords={job.total}
        fetchedRecords={job.fetched}
        onClose={closeExport}
        onCancel={cancelExport}
        onMinimize={minimizeExport}
      />

      {job.isOpen && job.isMinimized && isRunning && (
        <div className={styles.minimizedPill} onClick={restoreExport} role="button" tabIndex={0}>
          <Loader2 size={16} className={styles.spinnerIcon} />
          <span>
            {job.title || 'Exporting'} · {job.fetched.toLocaleString()}/{job.total > 0 ? job.total.toLocaleString() : '?'}
            {job.total > 0 ? ` (${pct}%)` : ''}
          </span>
          <button
            type="button"
            className={styles.minimizedPillCancel}
            onClick={(e) => { e.stopPropagation(); cancelExport(); }}
            title={t('common.cancel')}
          >
            <X size={14} />
          </button>
        </div>
      )}
    </ExportContext.Provider>
  );
};

export const useExport = () => {
  const context = useContext(ExportContext);
  if (!context) throw new Error('useExport must be used within an ExportProvider');
  return context;
};
